import type { NavGroup, NavItem } from "./types";

function formatBadgeCount(n: number): string {
  if (n > 99) return "99+";
  return String(n);
}

type SectionTotals = {
  training: number;
  communication: number;
  hr: number;
  documents: number;
};

function inferSectionKey(firstChildUrl: string): keyof SectionTotals | null {
  if (firstChildUrl.startsWith("/app/training")) return "training";
  if (firstChildUrl.startsWith("/app/communication")) return "communication";
  if (firstChildUrl.startsWith("/app/hr")) return "hr";
  if (firstChildUrl.startsWith("/app/documents")) return "documents";
  return null;
}

function mapNavItem(
  item: NavItem,
  leafCounts: Record<string, number>,
  sectionTotals: SectionTotals,
): NavItem {
  if ("url" in item && item.url) {
    const n = leafCounts[String(item.url)] ?? 0;
    return { ...item, badge: n > 0 ? formatBadgeCount(n) : undefined };
  }

  if ("items" in item && item.items?.length) {
    const firstUrl = String(item.items[0]?.url ?? "");
    const sectionKey = inferSectionKey(firstUrl);
    const parentTotal =
      sectionKey != null ? (sectionTotals[sectionKey] ?? 0) : 0;

    const items = item.items.map((sub) => {
      const n = leafCounts[String(sub.url)] ?? 0;
      return { ...sub, badge: n > 0 ? formatBadgeCount(n) : undefined };
    });

    return {
      ...item,
      items,
      badge: parentTotal > 0 ? formatBadgeCount(parentTotal) : undefined,
    };
  }

  return item;
}

/**
 * ໃສ່ badge ຕາມຈຳນວນແຈ້ງເຕືອນ: ແຕ່ລະລິ້ງຕາມ `leafCounts`, ເມນູຫຼັກທີ່ມີລູກຕາມ `sectionTotals` (ບໍ່ຊ້ຳບວກແຕ່ລະລູກ).
 */
export function mergeNavGroupsWithBadges(
  groups: NavGroup[],
  leafCounts: Record<string, number>,
  sectionTotals: SectionTotals,
): NavGroup[] {
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) =>
      mapNavItem(item, leafCounts, sectionTotals),
    ),
  }));
}
